import { FlatList, ListRenderItemInfo, StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import React, { useEffect, useMemo, useState } from 'react'
import { StackScreenProps } from '@react-navigation/stack'
import { useIsFocused } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { Contribution } from './types'
import { ContributionCard } from './components/contribution.card'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import { useTheme } from '../../../hooks/theme.hook'
import {
  ContributionStatus,
  ContributionType,
  useContributionsForMyArticlesQuery,
} from '../../../generated/graphql'
import ContributionTypeSelector from './components/contribution.type.selector'
import { SortSelector } from './components/sort.selector'
import _orderBy from 'lodash/orderBy'
import orderBy from 'lodash/orderBy'
import { useGlobalStyles } from '../../../contexts/global-styles.provider'
import { AppNavigatorParams } from '../../../navigation/types'

export function DecisionsScreen({
  route,
}: StackScreenProps<AppNavigatorParams, 'Decisions'>) {
  const theme = useTheme()
  const { t } = useTranslation('contributions')
  const globalStyles = useGlobalStyles()
  const [typeFilter, setTypeFilter] = useState<
    ContributionType | undefined | 'All'
  >(undefined)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const { loading, data, refetch } = useContributionsForMyArticlesQuery({
    variables: {
      status: ContributionStatus.Pending,
    },
    fetchPolicy: 'cache-and-network',
  })
  const isFocused = useIsFocused()
  useEffect(() => {
    if (isFocused) {
      refetch()
    }
  }, [isFocused])
  const contributions: Contribution[] = useMemo(
    () =>
      orderBy(
        (data?.contributionsForMyArticles || [])
          .filter((c) => c.contributionType != ContributionType.Reserved)
          .filter((c) => c.status == ContributionStatus.Pending)
          .filter((c) => !typeFilter || c.contributionType == typeFilter),
        ['createdAt'],
        [sortDirection]
      ),
    [data?.contributionsForMyArticles, typeFilter, sortDirection]
  )

  const styles = useMemo(
    () =>
      StyleSheet.create({
        empty: {
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
        },
        list: {
          marginTop: theme.spacing(5),
          flex: 1,
        },
        menuRow: {
          marginBottom: theme.spacing(3),
          marginHorizontal: theme.spacing(5),
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
        },
        sortBy: {},
        typeMenu: {
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'center',
          columnGap: theme.spacing(2),
        },
      }),
    [theme]
  )

  const renderItem = ({ item }: ListRenderItemInfo<Contribution>) => {
    return <ContributionCard key={item.id} contribution={item} />
  }

  return (
    <ScreenWrapper withScrollView={false} style={globalStyles.pageContainer}>
      <View style={styles.list}>
        <View style={styles.menuRow}>
          <View style={styles.sortBy}>
            <SortSelector
              direction={sortDirection}
              onDirection={setSortDirection}
            />
          </View>
          <View style={styles.typeMenu}>
            <Text>{t('Type')}</Text>
            <ContributionTypeSelector
              onSelect={setTypeFilter}
              selected={typeFilter}
            />
          </View>
        </View>
        {contributions.length > 0 ? (
          <FlatList
            style={{
              paddingHorizontal: theme.spacing(5),
            }}
            data={contributions}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            ListFooterComponent={<View style={{ height: 200 }}></View>}
          />
        ) : (
          <View style={styles.empty}>
            <Text
              variant={'titleMedium'}
              style={{ color: theme.palettes.neutral[70]! }}
            >
              {t('No decisions')}
            </Text>
          </View>
        )}
      </View>
    </ScreenWrapper>
  )
}

export default DecisionsScreen
